import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Select,
  Text,
  VStack,
} from '@chakra-ui/react';
import {TablerX} from '../../icons';
import usePlannerStyles from './usePlannerStyles';

const SCHEDULE_CARD_PADDING = {base: 4, md: 5};
const SCHEDULE_CLOSE_BUTTON_SIZE = '1.5rem';
const SCHEDULE_CLOSE_BUTTON_TOP = {
  base: 'calc(1rem + 0.4375rem)',
  md: 'calc(1.25rem + 0.4375rem)',
};
const SCHEDULE_CLOSE_BUTTON_X_ALIGN_OFFSET = '0.25rem';
const TIME_INPUT_FIELD_SX = {
  fontFamily: 'body',
  fontVariantNumeric: 'tabular-nums',
  '&::-webkit-datetime-edit': {
    fontFamily: 'inherit',
  },
  '&::-webkit-datetime-edit-fields-wrapper': {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.125rem',
  },
  '&::-webkit-datetime-edit-hour-field, &::-webkit-datetime-edit-minute-field, &::-webkit-datetime-edit-ampm-field':
    {
      border: '1px solid transparent',
      borderRadius: 'var(--chakra-radii-md)',
      fontFamily: 'inherit',
      lineHeight: '1.2',
      padding: '0.125rem 0.25rem',
    },
  '&::-webkit-datetime-edit-hour-field:focus, &::-webkit-datetime-edit-minute-field:focus, &::-webkit-datetime-edit-ampm-field:focus':
    {
      backgroundColor: 'var(--chakra-colors-opalite-50)',
      borderColor: 'var(--chakra-colors-opalite-500)',
      color: 'var(--chakra-colors-opalite-700)',
      outline: 'none',
    },
  '&::-webkit-datetime-edit-text': {
    color: 'var(--chakra-colors-gray-500)',
    fontFamily: 'inherit',
    padding: '0 0.125rem',
  },
  '&::-webkit-calendar-picker-indicator': {
    cursor: 'pointer',
    opacity: 0.72,
  },
  '&:focus-within': {
    borderColor: 'var(--chakra-colors-opalite-600)',
  },
};

const CreateSessionScheduleModal = ({
  dateOptions,
  isOpen,
  isSubmitting = false,
  onClose,
  onDateChange,
  onSubmit,
  onTimeChange,
  scheduledDateKey,
  scheduledTimeKey,
  userTimezoneLabel,
  visibleScheduleError,
}: Record<string, any>) => {
  const {
    borderColor,
    cardShadow,
    errorTextColor,
    focusOutlineStyles,
    inputFocusStyles,
    subtleText,
    surfaceBg,
    textColor,
  } = usePlannerStyles();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      size="xl"
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent
        aria-label="Schedule Encounter"
        bg="transparent"
        boxShadow="none"
        w="calc(100% - 2rem)"
        maxW="500px"
        mx={4}
      >
        <IconButton
          aria-label="Close"
          icon={<TablerX size={16} />}
          position="absolute"
          top={SCHEDULE_CLOSE_BUTTON_TOP}
          right={SCHEDULE_CARD_PADDING}
          transform={`translateX(${SCHEDULE_CLOSE_BUTTON_X_ALIGN_OFFSET})`}
          zIndex={1}
          boxSize={SCHEDULE_CLOSE_BUTTON_SIZE}
          minW={SCHEDULE_CLOSE_BUTTON_SIZE}
          p={0}
          bg="transparent"
          color={subtleText}
          borderWidth="0"
          borderRadius="0"
          variant="ghost"
          onClick={onClose}
          _hover={{bg: 'transparent', color: textColor}}
          _active={{bg: 'transparent'}}
          _focus={{bg: 'transparent'}}
          _focusVisible={focusOutlineStyles}
        />
        <ModalBody p={0}>
          <Box
            bg={surfaceBg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="xl"
            boxShadow={cardShadow}
            overflow="hidden"
          >
            <VStack spacing={4} align="stretch" p={SCHEDULE_CARD_PADDING}>
              <Text
                as="h2"
                id="schedule-encounter-heading"
                color={textColor}
                fontSize="2xl"
                fontWeight="semibold"
                lineHeight="1.25"
                letterSpacing="0"
                mb={0}
                pt={1}
                pb={2}
                pr={10}
                noOfLines={2}
                wordBreak="break-word"
              >
                Schedule Encounter
              </Text>

              <VStack spacing={4} align="stretch">
                <HStack spacing={3} align="flex-start">
                  <FormControl flex="2">
                    <FormLabel
                      color={textColor}
                      fontWeight="normal"
                      fontSize="sm"
                    >
                      Date
                    </FormLabel>
                    <Select
                      value={scheduledDateKey}
                      onChange={onDateChange}
                      aria-label="Scheduled date"
                      borderRadius="lg"
                      borderColor={borderColor}
                      h="44px"
                      minH="44px"
                      _hover={{borderColor}}
                      _focusVisible={inputFocusStyles}
                    >
                      {dateOptions.map((option: any) => (
                        <option key={option.key} value={option.key}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl flex="1">
                    <FormLabel
                      color={textColor}
                      fontWeight="normal"
                      fontSize="sm"
                    >
                      Time
                    </FormLabel>
                    <Input
                      type="time"
                      value={scheduledTimeKey}
                      onChange={onTimeChange}
                      aria-label="Scheduled time"
                      borderRadius="lg"
                      borderColor={borderColor}
                      h="44px"
                      minH="44px"
                      _hover={{borderColor}}
                      _focusVisible={inputFocusStyles}
                      sx={TIME_INPUT_FIELD_SX}
                    />
                  </FormControl>
                </HStack>

                {visibleScheduleError ? (
                  <Text
                    color={errorTextColor}
                    fontSize="sm"
                    fontWeight="normal"
                    role="alert"
                  >
                    {visibleScheduleError}
                  </Text>
                ) : (
                  <Text color={subtleText} fontSize="sm" fontWeight="normal">
                    {userTimezoneLabel ? `${userTimezoneLabel} · ` : ''}Between
                    30 minutes and 30 days from now.
                  </Text>
                )}
              </VStack>

              <HStack justify="flex-end" spacing={3} pt={2}>
                <Button
                  type="button"
                  variant="outline"
                  maxW="200px"
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="opalitePrimary"
                  maxW="200px"
                  onClick={onSubmit}
                  isLoading={isSubmitting}
                  loadingText="Scheduling"
                >
                  Schedule
                </Button>
              </HStack>
            </VStack>
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default CreateSessionScheduleModal;
